import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import frenchi18n from '@assets/i18n/fr.json';
import { FieldDto } from '@core/service/digital-factory/bank/types/bankDto';
import { environment } from '@environments/environment';
import { Bank } from '@modules/process/models/bank';
import { TranslateService } from '@ngx-translate/core';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { TrackerService } from '@shared/services/tracker.service';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import {
  BankFormDto,
  ConnectionFormItemDto,
  ConnectionRequestDto,
  LoginResponseDto,
} from './services/dtos/loginDtos';
import { LoginService } from './services/login.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'max-w-full d-flex flex-col justify-center items-center' },
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  @Input() errorMessage!: string | null;

  taggingPayload: string | undefined;
  isOtp = false;
  isLoaderOpen = false;
  isTestBankConnector = false;
  isOneyCustomBankConnector = false;
  isWebAuth = false;

  // Form can be both used for credentials & OTP
  // InitialForm would always be credentials form
  initialForm!: BankFormDto;
  form: UntypedFormGroup | null = null;

  constructor(
    private loginService: LoginService,
    private storeService: StoreService,
    private processEventService: ProcessEventService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private tracker: TrackerService
  ) {}

  _formData!: BankFormDto;

  get formData(): BankFormDto {
    return this._formData;
  }

  set formData(formData: BankFormDto) {
    const defaultValidators = [Validators.required];

    this._formData = formData;

    this.form = this.formBuilder.group({});

    this.formData.form.forEach((field) => {
      const fieldValidators = field.validation
        ? defaultValidators.concat([Validators.pattern(field.validation)])
        : defaultValidators;

      this.form?.addControl(field.name, new UntypedFormControl('', fieldValidators));

      this.form?.controls[field.name]?.valueChanges.subscribe(() =>
        this.form?.controls[field.name]?.markAsUntouched()
      );
    });
  }

  ngOnInit(): void {
    this.isOtp = false;

    this.isTestBankConnector =
      this.selectedBank.uid === environment.testBankConnector && environment.env !== 'prod';

    this.isOneyCustomBankConnector =
      this.selectedBank.uid === environment.oneyCustomBankConnector && environment.env !== 'prod';

    this.isWebAuth = this.selectedBank.authMechanism == 'webauth';
    this.taggingPayload = this.isWebAuth === true ? 'regional_bank_selection' : undefined;

    this.initialForm = { form: this.selectedBank.form };

    this.formData = this.initialForm;

    this.tracker.trackPage({ taggingPage: 'LOGIN', taggingPayload: this.taggingPayload });
  }

  async submit() {
    this.isLoaderOpen = true;

    if (this.isWebAuth) {
      this.submitWebAuth();
    } else if (this.isOtp) {
      this.submitOtp();
    } else {
      this.submitSignin();
    }
  }

  private formatForm(form: FieldDto[]): ConnectionFormItemDto[] {
    return form.map((field) => {
      return {
        name: field.name,
        value: this.form?.get(field.name)?.value as string,
      };
    });
  }

  async submitSignin(): Promise<void> {
    this.tracker.trackClick({
      click: frenchi18n.common_login,
      taggingPage: 'LOGIN',
      taggingPayload: this.taggingPayload,
    });

    const form = this.formatForm(this.formData.form);
    const request: ConnectionRequestDto = {
      form,
      external_bank_id: this.selectedBank.uid,
    };

    const connectionRequest = this.isOneyCustomBankConnector
      ? request
      : await this.loginService.encryptConnectionForm(request);

    this.loginService
      .connect({
        userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        connectionRequest,
        userId: this.storeService.getVariableOrThrow('USER_ID'),
      })
      .subscribe({
        next: this.handleConnectionResponse.bind(this),
        error: this.handleConnectionResponseError.bind(this),
      });
  }

  async submitOtp(): Promise<void> {
    this.tracker.trackClick({
      click: frenchi18n.common_validate,
      taggingPage: 'LOGIN',
      taggingPayload: this.taggingPayload,
    });

    const form = this.formatForm(this.formData.form);

    this.loginService
      .sendOneTimePassword({
        userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        connectionId: this.storeService.getVariableOrThrow('SELECTED_CONNECTION_ID'),
        form,
        userId: this.storeService.getVariableOrThrow('USER_ID'),
      })
      .subscribe({
        next: this.handleConnectionResponse.bind(this),
        error: this.handleConnectionResponseError.bind(this),
      });
  }

  // In some cases, webauth need a form to be sent (regional banks with Caisse d'Epargne for eg)
  async submitWebAuth(): Promise<void> {
    this.tracker.trackClick({
      click: frenchi18n.common_login,
      taggingPage: 'LOGIN',
      taggingPayload: this.taggingPayload,
    });

    const form = this.formatForm(this.formData.form);

    this.processEventService.push(ProcessEventFactory.SpawnLoginWebauthWithForm(form));
  }

  handleConnectionResponse(response: LoginResponseDto) {
    this.storeService.setVariable('SELECTED_CONNECTION_ID', response.connectionId);
    this.isLoaderOpen = false;
    this.errorMessage = null;
    this.isOtp = false;

    if (response.form && response.form.length > 0) {
      this.tracker.trackPage({ taggingPage: 'LOGIN', taggingPayload: 'OTP' });

      this.isOtp = true;
      this.formData = {
        form: response.form,
      };
    } else if (response.sca_third_party) {
      this.processEventService.push(ProcessEventFactory.SpawnScaEvent());
    } else if (response.accounts != null) {
      this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(response.accounts));
    }
  }

  handleConnectionResponseError(error: HttpErrorResponse) {
    this.tracker.trackPage({ taggingPage: 'LOGIN', taggingPayload: 'error' });

    console.log('connection error', error);

    this.isLoaderOpen = false;
    this.errorMessage = null;

    if (this.isOtp) {
      this.formData = this.initialForm;
      this.isOtp = false;
    }

    switch (error.error?.code) {
      case 'WRONG_PASSWORD':
        this.errorMessage = this.translate.instant('common_bank_wrong_creds');
        this.form?.markAsPristine();
        break;

      case 'ACTION_NEEDED':
      case 'API_RESPONSE_ERROR':
      default:
        this.processEventService.push(
          ProcessEventFactory.SpawnSelectedAccountsEvent('RETRY_BANK', [], 'bank_detail')
        );
        break;
    }
  }

  backToBankChoice() {
    this.tracker.trackClick({
      click: frenchi18n.common_return,
      taggingPage: 'LOGIN',
      taggingPayload: this.taggingPayload,
    });

    this.processEventService.push(ProcessEventFactory.SpawnGoBackToBankChoiceEvent());
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    this.backToBankChoice();
  }

  get selectedBank(): Bank {
    return this.storeService.getVariableOrThrow('SELECTED_BANK');
  }

  set selectedBank(value: Bank) {
    this.storeService.setVariable('SELECTED_BANK', value);
  }
}
